import { BaseModel } from '../base.model';

export enum MasterDatasetType {
  Paid = 'paid',
  Submit = 'submit',
}

export const REPORTING_SIGNALS_COLLECTION = 'signals';
export const REPORTING_MASTER_DATASET_SIGNALS_COLLECTION = 'master-datasets';
export const REPORTING_MASTER_DATASET_ACTIVITY_COLLECTION = 'master-dataset-activity';
export const REPORTING_MASTER_DATASET_SIGNAL_REPORT_DATES_COLLECTION = 'report-dates';
export const REPORTING_MASTER_DATASET_FILES_COLLECTION = 'master-datasets';

export const MASTER_DATASET_TYPES = Object.values(MasterDatasetType) as MasterDatasetType[];
const MASTER_DATASET_TYPE_SET = new Set<MasterDatasetType>(MASTER_DATASET_TYPES);

export enum MasterDatasetSourceType {
  MasterUpload = 'master_upload',
  CarrierPipeline = 'carrier_pipeline',
}

export const MASTER_DATASET_SOURCE_TYPES = Object.values(MasterDatasetSourceType) as MasterDatasetSourceType[];
const MASTER_DATASET_SOURCE_TYPE_SET = new Set<MasterDatasetSourceType>(MASTER_DATASET_SOURCE_TYPES);

export enum MasterDatasetRunStatus {
  Uploaded = 'uploaded',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export enum MasterDatasetProcessingStage {
  Downloading = 'downloading',
  Parsing = 'parsing',
  Preparing = 'preparing',
  Saving = 'saving',
  Finalizing = 'finalizing',
}

export const MASTER_DATASET_PROCESSING_STAGES = Object.values(
  MasterDatasetProcessingStage,
) as MasterDatasetProcessingStage[];

export const MASTER_DATASET_RUN_STATUSES = Object.values(MasterDatasetRunStatus) as MasterDatasetRunStatus[];

export enum MasterDatasetRunKeys {
  id = 'id',
  datasetType = 'dataset_type',
  sourceType = 'source_type',
  reportDate = 'report_date',
  uploadActionId = 'upload_action_id',
  sourceFileName = 'source_file_name',
  sourceFilePath = 'source_file_path',
  sourceContentType = 'source_content_type',
  sourceFileSize = 'source_file_size',
  status = 'status',
  processingErrorMessage = 'processing_error_message',
  recordsTotal = 'records_total',
  recordsValid = 'records_valid',
  recordsInvalid = 'records_invalid',
  isActive = 'is_active',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
}

export enum MasterDatasetRawRowKeys {
  runId = 'run_id',
  rowNumber = 'row_number',
  worksheetName = 'worksheet_name',
  rawPayload = 'raw_payload',
  createdAt = 'created_at',
}

export enum MasterRunSupportingSnapshotKeys {
  runId = 'run_id',
  agencyCodeNameMappingImportId = 'agency_code_name_mapping_import_id',
  agentSuppressionsImportId = 'agent_suppressions_import_id',
  agencyNtpImportId = 'agency_ntp_import_id',
  agencyRmdImportId = 'agency_rmd_import_id',
  resolvedAt = 'resolved_at',
  createdAt = 'created_at',
}

export interface MasterDatasetRun {
  [MasterDatasetRunKeys.id]: string;
  [MasterDatasetRunKeys.datasetType]: MasterDatasetType;
  [MasterDatasetRunKeys.sourceType]: MasterDatasetSourceType;
  [MasterDatasetRunKeys.reportDate]: string;
  [MasterDatasetRunKeys.uploadActionId]?: string | null;
  [MasterDatasetRunKeys.sourceFileName]?: string | null;
  [MasterDatasetRunKeys.sourceFilePath]?: string | null;
  [MasterDatasetRunKeys.sourceContentType]?: string | null;
  [MasterDatasetRunKeys.sourceFileSize]?: number | null;
  [MasterDatasetRunKeys.status]: MasterDatasetRunStatus;
  [MasterDatasetRunKeys.processingErrorMessage]?: string | null;
  [MasterDatasetRunKeys.recordsTotal]?: number | null;
  [MasterDatasetRunKeys.recordsValid]?: number | null;
  [MasterDatasetRunKeys.recordsInvalid]?: number | null;
  [MasterDatasetRunKeys.isActive]: boolean;
  [MasterDatasetRunKeys.createdAt]: Date | string;
  [MasterDatasetRunKeys.updatedAt]: Date | string;
}

export interface MasterRunSupportingSnapshot {
  [MasterRunSupportingSnapshotKeys.runId]: string;
  [MasterRunSupportingSnapshotKeys.agencyCodeNameMappingImportId]?: string | null;
  [MasterRunSupportingSnapshotKeys.agentSuppressionsImportId]?: string | null;
  [MasterRunSupportingSnapshotKeys.agencyNtpImportId]?: string | null;
  [MasterRunSupportingSnapshotKeys.agencyRmdImportId]?: string | null;
  [MasterRunSupportingSnapshotKeys.resolvedAt]: Date | string;
  [MasterRunSupportingSnapshotKeys.createdAt]: Date | string;
}

export interface MasterDatasetRawRow {
  [MasterDatasetRawRowKeys.runId]: string;
  [MasterDatasetRawRowKeys.rowNumber]: number;
  [MasterDatasetRawRowKeys.worksheetName]?: string | null;
  [MasterDatasetRawRowKeys.rawPayload]: Record<string, unknown>;
  [MasterDatasetRawRowKeys.createdAt]: Date | string;
}

export enum MasterDatasetSignalKeys {
  reportDate = 'reportDate',
  version = 'version',
  updatedAt = 'updatedAt',
  lastRunId = 'lastRunId',
  datasetType = 'datasetType',
  status = 'status',
}

export enum MasterDatasetActivityKeys {
  datasetType = 'datasetType',
  updatedAt = 'updatedAt',
  latestUploadByYear = 'latestUploadByYear',
  latestCrudUpdate = 'latestCrudUpdate',
}

export enum MasterDatasetCrudActivityEntryKeys {
  operation = 'operation',
  rowKey = 'rowKey',
  uid = 'uid',
  email = 'email',
  service = 'service',
  changeLogId = 'changeLogId',
  updatedAt = 'updatedAt',
}

export enum MasterDatasetUploadActivityEntryKeys {
  reportDate = 'reportDate',
  runId = 'runId',
  uploadActionId = 'uploadActionId',
  sourceFileName = 'sourceFileName',
  sourceFilePath = 'sourceFilePath',
  status = 'status',
  recordsTotal = 'recordsTotal',
  recordsValid = 'recordsValid',
  recordsInvalid = 'recordsInvalid',
  updatedAt = 'updatedAt',
}

export interface MasterDatasetCrudActivityEntry {
  [MasterDatasetCrudActivityEntryKeys.operation]: string | null;
  [MasterDatasetCrudActivityEntryKeys.rowKey]: string | null;
  [MasterDatasetCrudActivityEntryKeys.uid]: string | null;
  [MasterDatasetCrudActivityEntryKeys.email]: string | null;
  [MasterDatasetCrudActivityEntryKeys.service]: string | null;
  [MasterDatasetCrudActivityEntryKeys.changeLogId]: string | null;
  [MasterDatasetCrudActivityEntryKeys.updatedAt]: Date | null;
}

export interface MasterDatasetUploadActivityEntry {
  [MasterDatasetUploadActivityEntryKeys.reportDate]: string | null;
  [MasterDatasetUploadActivityEntryKeys.runId]: string | null;
  [MasterDatasetUploadActivityEntryKeys.uploadActionId]: string | null;
  [MasterDatasetUploadActivityEntryKeys.sourceFileName]: string | null;
  [MasterDatasetUploadActivityEntryKeys.sourceFilePath]: string | null;
  [MasterDatasetUploadActivityEntryKeys.status]: string | null;
  [MasterDatasetUploadActivityEntryKeys.recordsTotal]: number | null;
  [MasterDatasetUploadActivityEntryKeys.recordsValid]: number | null;
  [MasterDatasetUploadActivityEntryKeys.recordsInvalid]: number | null;
  [MasterDatasetUploadActivityEntryKeys.updatedAt]: Date | null;
}

export interface MasterDatasetActivityRecord {
  datasetType: MasterDatasetType;
  updatedAt: Date | null;
  latestUploadByYear: Record<string, MasterDatasetUploadActivityEntry>;
  latestCrudUpdate: MasterDatasetCrudActivityEntry | null;
}

export interface MasterDatasetSignal {
  [MasterDatasetSignalKeys.reportDate]: string;
  [MasterDatasetSignalKeys.version]: number;
  [MasterDatasetSignalKeys.updatedAt]: Date | string | null;
  [MasterDatasetSignalKeys.lastRunId]?: string | null;
  [MasterDatasetSignalKeys.datasetType]?: MasterDatasetType | null;
  [MasterDatasetSignalKeys.status]?: MasterDatasetRunStatus | null;
}

export enum MasterDatasetFileRecordKeys {
  datasetType = 'datasetType',
  reportDate = 'reportDate',
  runId = 'runId',
  uploadActionId = 'uploadActionId',
  sourceFileName = 'sourceFileName',
  sourceFilePath = 'sourceFilePath',
  sourceContentType = 'sourceContentType',
  sourceFileSize = 'sourceFileSize',
  processingStatus = 'processingStatus',
  processingStage = 'processingStage',
  processingProcessed = 'processingProcessed',
  processingTotal = 'processingTotal',
  processingStartedAt = 'processingStartedAt',
  processingErrorMessage = 'processingErrorMessage',
  recordsTotal = 'recordsTotal',
  recordsValid = 'recordsValid',
  recordsInvalid = 'recordsInvalid',
  updatedAt = 'updatedAt',
}

export interface MasterDatasetFileRecord extends BaseModel {
  [MasterDatasetFileRecordKeys.datasetType]: MasterDatasetType;
  [MasterDatasetFileRecordKeys.reportDate]: string;
  [MasterDatasetFileRecordKeys.runId]: string;
  [MasterDatasetFileRecordKeys.uploadActionId]?: string | null;
  [MasterDatasetFileRecordKeys.sourceFileName]?: string | null;
  [MasterDatasetFileRecordKeys.sourceFilePath]?: string | null;
  [MasterDatasetFileRecordKeys.sourceContentType]?: string | null;
  [MasterDatasetFileRecordKeys.sourceFileSize]?: number | null;
  [MasterDatasetFileRecordKeys.processingStatus]: MasterDatasetRunStatus;
  [MasterDatasetFileRecordKeys.processingStage]?: MasterDatasetProcessingStage | null;
  [MasterDatasetFileRecordKeys.processingProcessed]?: number | null;
  [MasterDatasetFileRecordKeys.processingTotal]?: number | null;
  [MasterDatasetFileRecordKeys.processingStartedAt]?: Date | string | null;
  [MasterDatasetFileRecordKeys.processingErrorMessage]?: string | null;
  [MasterDatasetFileRecordKeys.recordsTotal]?: number | null;
  [MasterDatasetFileRecordKeys.recordsValid]?: number | null;
  [MasterDatasetFileRecordKeys.recordsInvalid]?: number | null;
  [MasterDatasetFileRecordKeys.updatedAt]?: Date | string | null;
}

export function isMasterDatasetType(value: string): value is MasterDatasetType {
  return MASTER_DATASET_TYPE_SET.has(value as MasterDatasetType);
}

export function isMasterDatasetSourceType(value: string): value is MasterDatasetSourceType {
  return MASTER_DATASET_SOURCE_TYPE_SET.has(value as MasterDatasetSourceType);
}
